import { useState } from 'react';
import { Utensils } from 'lucide-react';
import { useFeedingStatus } from '../../../hooks/useFeedingStatus';
import { usePets } from '../../../contexts/PetsContext';
import { useFeedingGroups } from '../../../contexts/FeedingGroupsContext';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { computePortionGr } from '../../../services/petService';
import { formatGrams } from '../../../lib/format';

/**
 * Botón de "Alimentar" con manejo completo de flujos.
 *
 * Decisiones de UX:
 *  - Click normal con stock OK → alimenta de inmediato (no pide confirmación).
 *    La confirmación entorpece el flujo más común. Si hay error, lo deshace
 *    Cloud Function manualmente. Pero por la naturaleza atomic, esto es seguro.
 *  - Click si fue alimentado recientemente (<25% del intervalo) → modal de
 *    confirmación. Evita doble alimentación accidental.
 *  - Click sin stock suficiente → modal con dos opciones: cancelar o forceFeed.
 *
 * Tamaños:
 *  - 'compact' (para PetCard): solo ícono + label corta.
 *  - 'full' (para PetDetailPage): ícono + label larga.
 *
 * `fullWidth` permite que el variant compact ocupe todo el ancho del
 * contenedor (caso PetCard cuando convive con WaterButton en 50/50).
 */
const FeedingButton = ({ pet, variant = 'compact', fullWidth = false }) => {
  const { feedPet } = usePets();
  const { getGroupById } = useFeedingGroups();
  const toast = useToast();
  const feedingStatus = useFeedingStatus(pet);

  const [confirmingRecent, setConfirmingRecent] = useState(false);
  const [confirmingForce, setConfirmingForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const group = getGroupById(pet.feedingGroupId);
  const portionGr = computePortionGr(pet.dailyRationGr, pet.feedingsPerDay);
  // Si el grupo no se cargó todavía, asumimos que hay stock para no bloquear el botón.
  // El service tira INSUFFICIENT_STOCK si efectivamente no alcanza.
  // Stage 2: el stock disponible depende del modo del grupo:
  //  - Con dispensers (1+): hay stock si AL MENOS UNO cubre la porción.
  //  - Sin dispensers (modo "directo de bolsa"): la fuente es mainStockGr.
  const hasEnoughStock = (() => {
    if (!group) return true;
    const dispensers = group.dispensers ?? [];
    if (dispensers.length > 0) {
      return dispensers.some((d) => (d.stockGr ?? 0) >= portionGr);
    }
    return (group.mainStockGr ?? 0) >= portionGr;
  })();

  const executeFeed = async ({ forceFeed = false } = {}) => {
    try {
      setSubmitting(true);
      const result = await feedPet(pet.id, { forceFeed });
      if (result.forced) {
        toast.info(
          `Registrado: alimentaste a ${pet.name} sin tocar el stock contable.`
        );
      } else {
        toast.success(`${pet.name} comió. Descontamos ${formatGrams(result.portionGr)}.`);
      }
      setConfirmingRecent(false);
      setConfirmingForce(false);
    } catch (err) {
      toast.error(err.message || 'No se pudo registrar la alimentación.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    if (!hasEnoughStock) {
      setConfirmingForce(true);
      return;
    }
    if (feedingStatus.isRecentlyFed) {
      setConfirmingRecent(true);
      return;
    }
    executeFeed();
  };

  // Visual del botón principal
  const button = (
    <Button
      variant="primary"
      size={variant === 'full' ? 'lg' : 'md'}
      leftIcon={Utensils}
      onClick={handleClick}
      isLoading={submitting}
      disabled={submitting}
      fullWidth={variant === 'full' || fullWidth}
    >
      {variant === 'full' ? `Alimentar (${formatGrams(portionGr)})` : 'Alimentar'}
    </Button>
  );

  return (
    <>
      {button}

      {/* Modal: alimentar tras alimentación reciente */}
      <Modal
        isOpen={confirmingRecent}
        onClose={() => !submitting && setConfirmingRecent(false)}
        title="Alimentado recientemente"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            {pet.name} ya fue alimentada hace poco. ¿Querés registrar otra ración igual?
          </p>
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            Descontará <strong>{formatGrams(portionGr)}</strong> del dispenser.
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmingRecent(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              isLoading={submitting}
              onClick={() => executeFeed()}
            >
              Alimentar igual
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: sin stock, escape hatch */}
      <Modal
        isOpen={confirmingForce}
        onClose={() => !submitting && setConfirmingForce(false)}
        title="Sin stock en el dispenser"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            El dispenser no tiene suficiente para una ración completa
            ({formatGrams(portionGr)}). ¿Le diste comida a {pet.name} igual?
          </p>
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-100">
            Si confirmás, solo registramos la alimentación sin descontar stock contable.
            Después podés ajustar el inventario desde el detalle.
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmingForce(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              isLoading={submitting}
              onClick={() => executeFeed({ forceFeed: true })}
            >
              Alimenté igual
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FeedingButton;